import {HandleProps, HandleType} from 'reactflow';

import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

/**
 * Possible statuses for a node, which change the node's default styling. Warning and error styles cannot be
 * overridden.
 */
export enum NodeStatus {
  ERROR = 'ERROR',
  IN_PROGRESS = 'IN_PROGRESS',
  PENDING = 'PENDING',
  SUCCESS = 'SUCCESS',
  WARNING = 'WARNING',
}

/**
 * Icon type definition for nodes
 */
export type NodeIcon =
  | BenchlingIconName
  | {
      icon: BenchlingIconName;
      color?: string;
    };

/**
 * Handle interaction properties that can be overridden
 */
export type HandleInteractionProps = Partial<
  Pick<HandleProps, 'isConnectable' | 'onConnect' | 'isValidConnection'>
>;

/**
 * Section configuration for a node
 */
export interface NodeSection<TRowData> {
  sectionName: string;
  sectionIcon?: BenchlingIconName;
  sectionRows: Array<TRowData>;
  hideSection?: boolean;
}

/**
 * Props for the BenchlingNodeHeader component
 */
export interface BenchlingNodeHeaderProps {
  // Required
  nodeName: string;
  // Optional appearance

  // nodeIcon is the left-side icon in the node header
  nodeIcon?: NodeIcon;
  headerColor?: string;
  nodeSecondaryInfo?: string;

  // nodeStatus is the right-side status icon in the node header
  nodeStatus?: NodeStatus;

  // custom status icon, overrides default from nodeStatus
  headerIcon?: JSX.Element;
  disabled?: boolean;
  // Optional interactions
  onNodeHeaderClick?: (nodeId: string) => void;
}

/**
 * Core node configuration props
 */
export interface CoreBenchlingNodeDataBase<TRowData> extends BenchlingNodeHeaderProps {
  sections: Array<NodeSection<TRowData>>;
  hideSectionSummaries?: boolean;
}

// Warning and Error states should be standard across the platform and cannot be overridden.
export type NodeStatusStyles =
  | {
      nodeStatus: NodeStatus.WARNING | NodeStatus.ERROR;
      headerIcon?: never;
      headerColor?: never;
    }
  | {
      nodeStatus?: Exclude<NodeStatus, NodeStatus.WARNING | NodeStatus.ERROR>;
      headerIcon?: JSX.Element;
      headerColor?: string;
    };

export type CoreBenchlingNodeData<TRowData> = CoreBenchlingNodeDataBase<TRowData> & NodeStatusStyles;

/**
 * Props for rendering row handles
 */
export interface RowHandleProps<TRowData> {
  rowData: TRowData;
  handleType: HandleType;
}

/**
 * Props for rendering node handles
 */
export interface NodeHandleProps {
  nodeId: string;
  handleType: HandleType;
}

/**
 * Customization options for the BenchlingNode component
 * @template TRowData - The type of row data for the node
 */
export interface BenchlingNodeCustomization<TRowData> {
  rowRenderer: (rowData: TRowData) => JSX.Element | null;
  /**
   * Render handles in the node header. Returning null will suppress the handle, can be used
   * to conditionally render 0, 1, or both handles for a node.
   */
  nodeHeaderHandleRenderer?: (props: NodeHandleProps) => JSX.Element | null;
}

/**
 * Main node data type to use in ReactFlow that can be extended for custom nodes
 *
 * @example
 * // Custom row data
 * interface MyRowData { id: string; value: number }
 * const nodeData: BenchlingNodeData<MyRowData> = {
 *   nodeName: "Custom Node",
 *   sections: [{ sectionName: "Data", sectionRows: [{ id: "row1", value: 42 }] }],
 *   rowRenderer: (rowData) => <div>{rowData.value}</div>
 * };
 *
 * @example
 * // Custom node handle rendering
 * interface MyRowData { id: string; value: number }
 * const nodeData: BenchlingNodeData<MyRowData> = {
 *   nodeName: "Custom Node",
 *   sections: [{ sectionName: "Data", sectionRows: [{ id: "row1", value: 42 }] }],
 *   rowRenderer: (rowData) => <div>{rowData.value}</div>,
 *   // Only show a source handle for the node header
 *   nodeHeaderHandleRenderer: (props) => props.handleType === HandleType.SOURCE ? <Handle {...props} /> : null,
 * };
 */
export type BenchlingNodeData<TRowData> = CoreBenchlingNodeData<TRowData> &
  BenchlingNodeCustomization<TRowData>;
